// src/components/volwassenheid/VolwassenheidTab.jsx
import React, { useEffect, useMemo, useRef } from "react";
import { volwassenheidCatalog } from "../../content/volwassenheid.catalog";
import ActionChecklist from "../shared/ActionChecklist";
import UpdateSection from "../shared/UpdateSection";
import {
  Chart,
  RadarController,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend,
} from "chart.js";

Chart.register(
  RadarController,
  RadialLinearScale,
  PointElement,
  LineElement,
  Filler,
  Tooltip,
  Legend
);

// ─── helpers ────────────────────────────────────────────────────────────────

function san(str) {
  return String(str).replace(/\s+/g, "_");
}

function makeActionKeyVW(domain, deliverableId, actieId) {
  return `${san(domain)}_${san(deliverableId)}_${san(actieId)}`;
}

function makeUpdateKeyVW(domain, deliverableId) {
  return `${san(domain)}_${san(deliverableId)}`;
}

// ─── Niveau pills config ─────────────────────────────────────────────────────

const NIVEAU_PILLS = [
  { id: "niveau-1", label: "N1", cssClass: "n1" },
  { id: "niveau-2", label: "N2", cssClass: "n2" },
  { id: "niveau-3", label: "N3", cssClass: "n3" },
  { id: "niveau-4", label: "N4", cssClass: "n4" },
  { id: "niveau-5", label: "N5", cssClass: "n5" },
];

// ─── Praatplaat configuration ──────────────────────────────────────────────

const PRAATPLAAT_COLUMNS = [
  {
    id: "left",
    pijlerId: "informatie-gedreven-besturen",
    heading: "INFORMATIE GEDREVEN BESTUREN",
    headingColor: "#31719D",
    nodeColor: "#E0EDF5",
    deliverables: [
      { label: "Opgaven en ambities", slug: "opgaven-en-ambities" },
      { label: "Dashboard",          slug: "dashboard" },
      { label: "Datageletterdheid",  slug: "datageletterdheid" },
    ],
  },
  {
    id: "mid",
    pijlerId: "digitale-bedrijfsvoering",
    heading: "DIGITALE BEDRIJFSVOERING",
    headingColor: "#A7522E",
    nodeColor: "#FDDDD6",
    deliverables: [
      { label: "Definitiehandboek", slug: "definitiehandboek" },
      { label: "Bedrijfsfuncties",  slug: "bedrijfsfuncties" },
      { label: "Kernprocessen",     slug: "kernprocessen" },
      { label: "KPI's",             slug: "kpis" },
    ],
  },
  {
    id: "right",
    pijlerId: "strategisch-datamanagement",
    heading: "STRATEGISCH DATAMANAGEMENT",
    headingColor: "#AF8931",
    nodeColor: "#EEE1C4",
    deliverables: [
      { label: "Data-governance",       slug: "data-governance" },
      { label: "Data-architectuur",     slug: "data-architectuur" },
      { label: "Data-bronnen",          slug: "databronnen" },
      { label: "Data-kwaliteit",        slug: "datakwaliteit" },
      { label: "Data-uitwisselbaarheid", slug: "data-uitwisselbaarheid" },
    ],
  },
];

function getActivePill(activeNiveau) {
  return NIVEAU_PILLS.find((p) => p.id === activeNiveau) ?? NIVEAU_PILLS[0];
}

function getDeliverablePct(pijlerId, slug, activeNiveau, domain, checkedActions) {
  const pijler = volwassenheidCatalog.pijlers.find((p) => p.id === pijlerId);
  if (!pijler) return null;
  const niveau = pijler.niveaus.find((n) => n.id === `${pijlerId}.${activeNiveau}`);
  if (!niveau) return null;
  const fullId = `${pijlerId}.${activeNiveau}.${slug}`;
  const deliverable = niveau.deliverables.find((d) => d.id === fullId);
  if (!deliverable) return null;
  const actions = deliverable.actiepunten || [];
  if (actions.length === 0) return 0;
  const done = actions.filter((a) =>
    checkedActions[makeActionKeyVW(domain, deliverable.id, a.id)]
  ).length;
  return Math.round((done / actions.length) * 100);
}

function getDeliverablePctAllDomains(pijlerId, slug, activeNiveau, domains, checkedActions) {
  const pijler = volwassenheidCatalog.pijlers.find((p) => p.id === pijlerId);
  if (!pijler) return null;
  const niveau = pijler.niveaus.find((n) => n.id === `${pijlerId}.${activeNiveau}`);
  if (!niveau) return null;
  const fullId = `${pijlerId}.${activeNiveau}.${slug}`;
  const deliverable = niveau.deliverables.find((d) => d.id === fullId);
  if (!deliverable) return null;
  const actions = deliverable.actiepunten || [];
  if (actions.length === 0) return 0;
  const totalPossible = actions.length * domains.length;
  if (totalPossible === 0) return 0;
  let totalDone = 0;
  for (const domain of domains) {
    for (const a of actions) {
      if (checkedActions[makeActionKeyVW(domain, deliverable.id, a.id)]) {
        totalDone++;
      }
    }
  }
  return Math.round((totalDone / totalPossible) * 100);
}

// ─── Radar helpers ──────────────────────────────────────────────────────────

function hexToRgba(hex, alpha) {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function getDeliverableAchievedLevel(pijlerId, slug, domain, checkedActions) {
  const pijler = volwassenheidCatalog.pijlers.find((p) => p.id === pijlerId);
  if (!pijler) return 0;
  let score = 0;
  for (let n = 1; n <= 5; n++) {
    const niveauId = `${pijlerId}.niveau-${n}`;
    const niveau = pijler.niveaus.find((nv) => nv.id === niveauId);
    if (!niveau) continue;
    const fullId = `${pijlerId}.niveau-${n}.${slug}`;
    const deliverable = niveau.deliverables.find((d) => d.id === fullId);
    if (!deliverable) continue;
    const actions = deliverable.actiepunten || [];
    if (actions.length === 0) continue;
    const done = actions.filter(
      (a) => checkedActions[makeActionKeyVW(domain, deliverable.id, a.id)]
    ).length;
    score += done / actions.length;
  }
  return score;
}

function getDeliverableAchievedLevelAllDomains(pijlerId, slug, domains, checkedActions) {
  if (!domains || domains.length === 0) return 0;
  let total = 0;
  for (const domain of domains) {
    total += getDeliverableAchievedLevel(pijlerId, slug, domain, checkedActions);
  }
  return total / domains.length;
}

// ─── Praatplaat overview card ───────────────────────────────────────────────

function PraatplaatOverviewCard({ activeNiveau, domain, domains, checkedActions }) {
  const pill = getActivePill(activeNiveau);
  const isTotaal = domain === "__totaaloverzicht__";

  return (
    <div className="card-3d vw-praatplaat-shell">
      <div className="vw-praatplaat-grid">
        {PRAATPLAAT_COLUMNS.map((col) => (
          <div key={col.id} className={`vw-praatplaat-col vw-praatplaat-col--${col.id}`}>
            {/* Colored header */}
            <div className="vw-praatplaat-header" style={{ background: col.headingColor }}>
              {col.id === "left" && (
                <svg viewBox="0 0 36 28" width="28" height="22" fill="none">
                  <path d="M4 24A14 14 0 0 1 32 24" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" />
                  <line x1="18" y1="24" x2="12" y2="12" stroke="#fff" strokeWidth="2" strokeLinecap="round" />
                  <circle cx="18" cy="24" r="2" fill="#fff" />
                </svg>
              )}
              {col.id === "mid" && (
                <svg viewBox="0 0 36 36" width="28" height="28" fill="none">
                  <rect x="11" y="2" width="14" height="8" rx="2" stroke="#fff" strokeWidth="2" />
                  <rect x="1" y="26" width="12" height="8" rx="2" stroke="#fff" strokeWidth="2" />
                  <rect x="23" y="26" width="12" height="8" rx="2" stroke="#fff" strokeWidth="2" />
                  <line x1="18" y1="10" x2="18" y2="18" stroke="#fff" strokeWidth="2" />
                  <line x1="7" y1="18" x2="29" y2="18" stroke="#fff" strokeWidth="2" />
                  <line x1="7" y1="18" x2="7" y2="26" stroke="#fff" strokeWidth="2" />
                  <line x1="29" y1="18" x2="29" y2="26" stroke="#fff" strokeWidth="2" />
                </svg>
              )}
              {col.id === "right" && (
                <svg viewBox="0 0 32 36" width="24" height="28" fill="none">
                  <ellipse cx="16" cy="8" rx="12" ry="5" stroke="#fff" strokeWidth="2" />
                  <path d="M4 8v20c0 2.76 5.37 5 12 5s12-2.24 12-5V8" stroke="#fff" strokeWidth="2" />
                  <path d="M4 18c0 2.76 5.37 5 12 5s12-2.24 12-5" stroke="#fff" strokeWidth="2" />
                </svg>
              )}
              <span>{col.heading}</span>
            </div>

            {/* Deliverable nodes */}
            <div className="vw-praatplaat-tree">
              {col.deliverables.map((d) => {
                const pct = isTotaal
                  ? getDeliverablePctAllDomains(col.pijlerId, d.slug, activeNiveau, domains, checkedActions)
                  : getDeliverablePct(col.pijlerId, d.slug, activeNiveau, domain, checkedActions);
                return (
                  <div
                    key={d.slug}
                    className={`vw-praatplaat-node vw-praatplaat-node--${col.id}`}
                    style={{ background: col.nodeColor }}
                  >
                    <span className={`vw-praatplaat-pill-inline niveau-pill ${pill.cssClass} active`}>
                      {pill.label}
                    </span>
                    <span className="vw-praatplaat-node-label">{d.label}</span>
                    {pct !== null && (
                      <span className="vw-praatplaat-pct">{pct}%</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── Combined radar (all 12 deliverables) ──────────────────────────────────

const ALL_DELIVERABLES = PRAATPLAAT_COLUMNS.flatMap((col) =>
  col.deliverables.map((d) => ({
    ...d,
    pijlerId: col.pijlerId,
    color: col.headingColor,
  }))
);

function CombinedRadarCard({ domain, domains, checkedActions }) {
  const canvasRef = useRef(null);
  const chartRef = useRef(null);
  const isTotaal = domain === "__totaaloverzicht__";

  const model = useMemo(() => {
    const labels = ALL_DELIVERABLES.map((d) => d.label);
    const values = ALL_DELIVERABLES.map((d) =>
      isTotaal
        ? getDeliverableAchievedLevelAllDomains(d.pijlerId, d.slug, domains, checkedActions)
        : getDeliverableAchievedLevel(d.pijlerId, d.slug, domain, checkedActions)
    );
    const colors = ALL_DELIVERABLES.map((d) => d.color);
    return { labels, values, colors };
  }, [domain, domains, checkedActions, isTotaal]);

  useEffect(() => {
    if (!canvasRef.current) return;
    if (chartRef.current) {
      chartRef.current.destroy();
      chartRef.current = null;
    }

    const ctx = canvasRef.current.getContext("2d");
    chartRef.current = new Chart(ctx, {
      type: "radar",
      data: {
        labels: model.labels,
        datasets: [
          {
            label: "",
            data: model.values,
            fill: true,
            borderColor: "#3C5595",
            borderWidth: 2.5,
            backgroundColor: hexToRgba("#3C5595", 0.12),
            pointBackgroundColor: model.colors,
            pointBorderColor: "#ffffff",
            pointBorderWidth: 2,
            pointRadius: 5,
            pointHoverRadius: 7,
            tension: 0.25,
          },
        ],
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: { display: false },
          tooltip: {
            callbacks: {
              label: (ctx) => `Niveau ${ctx.parsed.r.toFixed(1)}`,
            },
          },
        },
        scales: {
          r: {
            min: 0,
            max: 5,
            ticks: {
              stepSize: 1,
              callback: (v) => (v > 0 ? `N${v}` : ""),
              backdropColor: "transparent",
              font: { size: 11, weight: "600" },
            },
            pointLabels: {
              font: { size: 12, weight: "700" },
              color: model.colors,
            },
            grid: {
              circular: false,
              color: "rgba(148, 163, 184, 0.3)",
            },
            angleLines: {
              color: "rgba(148, 163, 184, 0.3)",
            },
          },
        },
      },
    });

    return () => {
      if (chartRef.current) {
        chartRef.current.destroy();
        chartRef.current = null;
      }
    };
  }, [model]);

  return (
    <div className="card-3d vw-radar-shell">
      <div className="vw-radar-canvas-wrap vw-radar-canvas-wrap--full">
        <canvas ref={canvasRef} />
      </div>
    </div>
  );
}

// ─── Werkinstructie bullets (array variant) ──────────────────────────────────

function WerkinstructieList({ items }) {
  const list = items || [];
  return (
    <ul className="text-[12px] text-slate-700 space-y-1 mb-1">
      {list.map((s, i) => (
        <li key={i} className="flex gap-1">
          <span className="mt-[3px] text-[10px] text-sky-500">●</span>
          <span>{s.endsWith(".") ? s : s + "."}</span>
        </li>
      ))}
    </ul>
  );
}

// ─── Deliverable card ────────────────────────────────────────────────────────

function DeliverableCard({
  deliverable,
  domain,
  checkedActions,
  updates,
  userEmail,
  onToggle,
  onSaveUpdate,
  onDeleteUpdate,
}) {
  const actions = deliverable.actiepunten || [];
  const actionKeys = actions.map((a) =>
    makeActionKeyVW(domain, deliverable.id, a.id)
  );
  const updateKey = makeUpdateKeyVW(domain, deliverable.id);

  return (
    <div className="card-3d rounded-2xl p-4 flex flex-col gap-3">
      {/* Titel & icon */}
      <div className="flex items-center gap-3">
        <div
          className="flex items-center justify-center rounded-xl"
          style={{
            width: 40,
            height: 40,
            background: "linear-gradient(145deg,#eff6ff,#dbeafe)",
            boxShadow:
              "0 4px 10px rgba(37,99,235,0.25), inset 0 1px 0 rgba(255,255,255,0.9)",
          }}
        >
          <span className="text-xl">{deliverable.icon}</span>
        </div>
        <div className="text-slate-900 text-xl font-extrabold leading-tight">
          {deliverable.title}
        </div>
      </div>

      {/* Hoofdvraag */}
      {deliverable.hoofdvraag && (
        <div>
          <div className="section-label">Hoofdvraag</div>
          <p className="text-[13px] text-slate-700 italic leading-snug">
            {deliverable.hoofdvraag}
          </p>
        </div>
      )}

      {/* Beoogd resultaat */}
      {deliverable.beoogd_resultaat && (
        <div>
          <div className="section-label">Beoogd resultaat</div>
          <p className="text-[12px] text-slate-700 leading-snug">
            {deliverable.beoogd_resultaat}
          </p>
        </div>
      )}

      {/* Werkinstructie */}
      <div>
        <div className="section-label">Werkinstructie</div>
        <WerkinstructieList items={deliverable.werkinstructies} />
      </div>

      {/* Actiepunten */}
      <ActionChecklist
        actions={actions}
        actionKeys={actionKeys}
        checkedActions={checkedActions}
        onToggle={onToggle}
      />

      {/* Update */}
      <UpdateSection
        updateKey={updateKey}
        updates={updates}
        userEmail={userEmail}
        onSaveUpdate={onSaveUpdate}
        onDeleteUpdate={onDeleteUpdate}
      />
    </div>
  );
}

// ─── Main export ─────────────────────────────────────────────────────────────

export default function VolwassenheidTab({
  pijlerId,
  activeNiveau,
  onSetNiveau,
  domain,
  domains,
  checkedActions,
  updates,
  userEmail,
  onToggle,
  onSaveUpdate,
  onDeleteUpdate,
}) {
  const isOverview = pijlerId === "overview";

  const pijler = useMemo(
    () => volwassenheidCatalog.pijlers.find((p) => p.id === pijlerId) ?? null,
    [pijlerId]
  );

  // Find the niveau within the selected pijler (if not overview)
  const niveau = useMemo(() => {
    if (!pijler) return null;
    return pijler.niveaus.find((n) => n.id.endsWith(activeNiveau)) ?? null;
  }, [pijler, activeNiveau]);

  const pijlerLabel = niveau ? niveau.title : "Totaaloverzicht";

  return (
    <div className="space-y-4">
      {/* Tab header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3 mb-2">
        <div>
          <div
            className="text-xs font-semibold uppercase tracking-wide"
            style={{ color: "#ffffff" }}
          >
            {isOverview ? "Totaaloverzicht" : pijlerLabel}
          </div>
          <div className="text-sm text-slate-200">
            {isOverview
              ? (domain === "__totaaloverzicht__"
                  ? "Overzicht van voortgang over alle domeinen."
                  : "Overzicht van alle pijlers en voortgang per domein.")
              : (niveau?.cmmi_uitleg
                  ?? "Werk de deliverables af en geef updates over de voortgang van deze pijler.")}
          </div>
        </div>

        <div className="flex items-center gap-3 flex-wrap justify-end">
          {/* Niveau-pills */}
          <div className="flex items-center gap-1">
            {NIVEAU_PILLS.map((pill) => (
              <button
                key={pill.id}
                type="button"
                className={`niveau-pill ${pill.cssClass}${activeNiveau === pill.id ? " active" : ""}`}
                onClick={() => onSetNiveau(pill.id)}
                title={`Niveau ${pill.label}`}
              >
                {pill.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Content */}
      {isOverview ? (
        <>
          <PraatplaatOverviewCard activeNiveau={activeNiveau} domain={domain} domains={domains} checkedActions={checkedActions} />
          <CombinedRadarCard domain={domain} domains={domains} checkedActions={checkedActions} />
        </>
      ) : (
        <div className="grid gap-4 md:grid-cols-2">
          {(niveau?.deliverables ?? []).map((deliverable) => (
            <DeliverableCard
              key={deliverable.id}
              deliverable={deliverable}
              domain={domain}
              checkedActions={checkedActions}
              updates={updates}
              userEmail={userEmail}
              onToggle={onToggle}
              onSaveUpdate={onSaveUpdate}
              onDeleteUpdate={onDeleteUpdate}
            />
          ))}
        </div>
      )}
    </div>
  );
}
